import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { toast } from "sonner";

const PLATFORMS = [
  {
    id: "meta",
    name: "Meta Ads",
    detail: "Facebook, Instagram e Lead Ads",
    color: "bg-blue-600",
  },
  {
    id: "tiktok",
    name: "TikTok Ads",
    detail: "Campanhas, Instant Forms e leads",
    color: "bg-slate-900",
  },
];

function displayText(value, fallback) {
  if (typeof value === "string" && value.trim()) return value;
  if (value && typeof value === "object")
    return value.error || value.message || fallback;
  return fallback;
}

export default function SocialIntegrations({ api }) {
  const [connections, setConnections] = useState({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const results = await Promise.all(
      PLATFORMS.map(async ({ id }) => {
        try {
          if (id === "meta") {
            const { data } = await api.get("/meta/ad-accounts");
            const accounts =
              data?.adAccounts || (Array.isArray(data) ? data : []);
            return [
              id,
              {
                status: "connected",
                accounts_count: accounts.length,
                message: accounts.length
                  ? "Conexão Meta ativa no Synkly."
                  : "Conexão Meta ativa, mas sem contas de anúncio disponíveis.",
              },
            ];
          }
          const { data } = await api.get(`/ads/${id}/connection`);
          return [id, data];
        } catch (error) {
          return [
            id,
            {
              status: "not_configured",
              message: displayText(
                error?.response?.data?.detail,
                "Synkly não configurado",
              ),
            },
          ];
        }
      }),
    );
    setConnections(Object.fromEntries(results));
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const openSynkly = async (platform) => {
    if (platform === "meta") {
      toast.info(
        "A conexão OAuth e a seleção do Business são feitas no painel administrativo do Synkly.",
      );
      return;
    }
    try {
      const { data } = await api.get(`/ads/${platform}/connection`);
      if (data.setup_url)
        window.open(data.setup_url, "_blank", "noopener,noreferrer");
      else
        toast.info(
          "Peça ao administrador do Synkly para habilitar o fluxo OAuth desta plataforma.",
        );
    } catch {
      toast.error(
        "Configure primeiro a URL e a API key do Synkly em Integrações Gerais.",
      );
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">
          Redes Sociais e Ads
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Conexões OAuth, permissões e webhooks são gerenciados com segurança
          pelo Synkly.
        </p>
      </div>
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4 text-sm text-blue-900">
          O NexoSynX não recebe nem armazena tokens, App Secrets ou Webhook
          Secrets. Caso uma conexão não esteja disponível, use o botão de
          configuração para concluir o conector no Synkly.
        </CardContent>
      </Card>
      <div className="grid gap-4 md:grid-cols-2">
        {PLATFORMS.map((platform) => {
          const connection = connections[platform.id] || {};
          const connected = connection.status === "connected";
          return (
            <Card key={platform.id}>
              <CardHeader>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <CardTitle>{platform.name}</CardTitle>
                    <CardDescription className="mt-1">
                      {platform.detail}
                    </CardDescription>
                  </div>
                  {platform.id === "meta" &&
                    connected &&
                    connection.accounts_count !== undefined && (
                      <div className="flex items-center gap-2">
                        {/* Placeholder for Meta Icon */}
                        <span>{connection.accounts_count} Contas</span>
                      </div>
                    )}
                  {!(
                    platform.id === "meta" &&
                    connected &&
                    connection.accounts_count !== undefined
                  ) && (
                    <Badge
                      className={connected ? "bg-green-600" : "bg-amber-500"}
                    >
                      {connected ? "Conectado" : "Pendente"}
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-slate-600">
                  {loading
                    ? "Verificando conexão..."
                    : displayText(
                        connection.message,
                        connected
                          ? "Conta e permissões válidas."
                          : "Conclua a conexão no Synkly.",
                      )}
                </p>
                {connection.accounts_count !== undefined && (
                  <p className="text-xs text-slate-500">
                    {connection.accounts_count} conta(s) disponível(is)
                  </p>
                )}
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className={platform.color}
                    onClick={() => openSynkly(platform.id)}
                  >
                    Configurar no Synkly
                  </Button>
                  <Button size="sm" variant="outline" onClick={load}>
                    Atualizar status
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
